import React from 'react';
import type { Id as ToastID, ToastOptions } from 'react-toastify';
import { toast } from 'react-toastify';

export const DEFAULT_DEBOUNCE_DURATION = 500;
export enum NotificationType {
  INFO,
  SUCCESS,
  WARNING,
  ERROR,
  PROMISE,
  LOADING,
}

const ToastIcon = ({ className, prefix = 'fas' }: { className: string; prefix?: string }) => {
  return <i className={`${prefix} fa-${className}`} />;
};

const _toast = (
  text: string | React.ReactNode,
  type: NotificationType,
  options: ToastOptions,
  toastId?: ToastID,
): ToastID | undefined => {
  switch (type) {
    case NotificationType.WARNING:
      if (toastId) {
        toast.update(toastId, {
          render: text,
          type: 'warning',
          icon: <ToastIcon className="diamond-exclamation" />,
          ...options,
        });
        return toastId;
      }

      return toast.warning(text, {
        icon: <ToastIcon className="diamond-exclamation" />,
        ...options,
      });

    case NotificationType.ERROR:
      if (toastId) {
        toast.update(toastId, {
          render: text,
          type: 'error',
          icon: <ToastIcon className="exclamation-triangle" />,
          ...options,
        });
        return toastId;
      }

      return toast.error(text, {
        icon: <ToastIcon className="exclamation-triangle" />,
        ...options,
      });

    case NotificationType.SUCCESS:
      if (toastId) {
        toast.update(toastId, {
          render: text,
          type: 'success',
          icon: <ToastIcon className="check" />,
          ...options,
        });
        return toastId;
      }

      return toast.success(text, {
        icon: <ToastIcon className="check" />,
        ...options,
      });

    case NotificationType.PROMISE: {
      const promise = new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      toast.promise(
        promise,
        {
          pending: text as string,
          success: {
            render() {
              return `${text} succeeded`;
            },
            icon: <ToastIcon className="check" />,
          },
          error: {
            render() {
              return `${text} failed`;
            },
            icon: <ToastIcon className="exclamation-triangle" />,
          },
        },
        options,
      );
      break;
    }

    case NotificationType.LOADING:
      return toast.loading(text, options);

    case NotificationType.INFO:
    default:
      if (toastId) {
        toast.update(toastId, {
          render: text,
          type: 'info',
          icon: <ToastIcon className="exclamation-circle" />,
          ...options,
        });
        return toastId;
      }

      return toast.info(text, {
        icon: <ToastIcon className="exclamation-circle" />,
        ...options,
      });
  }

  return toastId;
};

export const Notifications = {
  dismissAll() {
    toast.dismiss();
  },
  dismiss(toastId: ToastID) {
    toast.dismiss(toastId);
  },
  isActive(toastId: ToastID) {
    return toast.isActive(toastId);
  },

  notifyPersistent: (
    text: string | React.ReactNode,
    type: NotificationType = NotificationType.INFO,
    overrideIcon?: ToastOptions['icon'],
  ): ToastID | undefined => {
    const autoClose = type !== NotificationType.WARNING && type !== NotificationType.ERROR;
    const closeOnClick = type !== NotificationType.LOADING && type !== NotificationType.PROMISE;

    const options = {
      position: 'top-center',
      containerId: 'wide',
      autoClose,
      closeOnClick,
      ...(overrideIcon ? { icon: overrideIcon } : {}),
    } as ToastOptions;

    return _toast(text, type, options);
  },

  notifyPersistentCustom: (text: string | React.ReactNode, icon: string, autoClose: boolean): ToastID | undefined => {
    const options = {
      position: 'top-center',
      containerId: 'wide',
      autoClose,
      closeOnClick: true,
      icon: <ToastIcon className={icon} />,
    } as ToastOptions;

    return _toast(text, NotificationType.INFO, options);
  },

  notify: (
    text: string | React.ReactNode,
    type: NotificationType = NotificationType.INFO,
    position = 'bottom-right',
    className?: string,
  ): ToastID | undefined => {
    const autoClose = type !== NotificationType.WARNING && type !== NotificationType.ERROR;
    const closeOnClick = type !== NotificationType.LOADING && type !== NotificationType.PROMISE;

    const options = {
      position,
      closeButton: false,
      containerId: 'default',
      autoClose,
      closeOnClick,
      ...(className ? { className } : {}),
    } as ToastOptions;

    return _toast(text, type, options);
  },

  notifyUpdate: (
    toastId: ToastID | undefined,
    text: string | React.ReactNode,
    type: NotificationType = NotificationType.INFO,
  ): ToastID | undefined => {
    const autoClose = type !== NotificationType.WARNING && type !== NotificationType.ERROR;
    const closeOnClick = type !== NotificationType.LOADING && type !== NotificationType.PROMISE;
    const isLoading = type === NotificationType.LOADING;

    const options = {
      position: 'bottom-right',
      closeButton: false,
      containerId: 'default',
      autoClose,
      closeOnClick,
      isLoading,
    } as ToastOptions;

    return _toast(text, type, options, toastId);
  },

  notifyUpdatePersistent: (
    toastId: ToastID | undefined,
    text: string | React.ReactNode,
    type: NotificationType = NotificationType.INFO,
    overrideIcon?: ToastOptions['icon'],
  ): ToastID | undefined => {
    const autoClose = type === NotificationType.WARNING || type === NotificationType.ERROR ? false : undefined;
    const closeOnClick = type !== NotificationType.LOADING && type !== NotificationType.PROMISE;

    const options = {
      position: 'top-center',
      closeButton: false,
      containerId: 'wide',
      autoClose,
      closeOnClick,
      ...(overrideIcon ? { icon: overrideIcon } : {}),
    } as ToastOptions;

    return _toast(text, type, options, toastId);
  },
};
